import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { EMPTY, forkJoin, Observable } from 'rxjs';
import { map, mergeMap, switchMap, tap, timeout } from 'rxjs/operators';
import { CommonService } from '../../../_common/services/common.service';
import { MenuService } from '../../../_common/services/menu.service';
import { EmployerClientService } from '../../../_common/services/service-clients/employer-client.service';
import { HttpClientService } from '../../../_common/services/service-clients/http-client.service';
import { BaseStateActions } from '../../../_common/state/base/base.actions';
import { SettingsService } from '../../settings/settings.service';
import { User } from './../../../_common/model/user';
import { ApiUrlService } from './../../../_common/services/api-url.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private readonly LOGIN_FORM_TIMEOUT = 5000;

    constructor(
        private httpClient: HttpClientService,
        private helper: ApiUrlService,
        private store: Store,
        private router: Router,
        private commonRest: CommonService,
        private settingsRest: SettingsService,
        private employerClientService: EmployerClientService,
        private menuService: MenuService,
    ) {}

    public getUserData(): Observable<User> {
        return this.httpClient.get(this.helper.getAssistanceUrl(2, 'userinfo/'));
    }

    public getContractorUserData(): Observable<User> {
        return this.httpClient.get(this.helper.getEambulantaApi(2, 'userinfo/'));
    }

    /**
     * Initialize business subject data for a specific business subject ID
     * This method can be reused for both initial login and business subject switching
     */
    public initializeBusinessSubjectData(bsId: number): Observable<any> {
        return forkJoin({
            bsSettings: this.commonRest.getBSGuiSettings(bsId),
            modules: this.commonRest.getBSModules(bsId),
            sendingRules: this.commonRest.getSendingRulesForBS(bsId),
            employerId: this.employerClientService.getEmployerIdByBsId(bsId),
        }).pipe(
            switchMap(val => {
                return this.store
                    .dispatch([
                        new BaseStateActions.Common.SetBsGuiSettings(val.bsSettings),
                        new BaseStateActions.Bs.SetBsModules(val.modules),
                        new BaseStateActions.Common.SetBSSendingRules(val.sendingRules),
                        new BaseStateActions.Common.SetEmployerId(val.employerId),
                    ])
                    .pipe(
                        switchMap(() => {
                            return this.settingsRest
                                .getEmployer(val.employerId)
                                .pipe(tap(employer => this.store.dispatch(new BaseStateActions.Common.SetBsAdditionalInfo(employer))));
                        }),
                    );
            }),
        );
    }

    public initializeUserData(tokenModel: any): Observable<any> {
        return this.getUserData().pipe(
            switchMap(user => {
                return this.store.dispatch(new BaseStateActions.Common.SetUser(user)).pipe(map(() => user));
            }),
            mergeMap(user => {
                if (user?.businessSubjects.length > 0) {
                    const bsid = user?.businessSubjects[0]?.id;

                    return this.initializeBusinessSubjectData(bsid).pipe(
                        switchMap(() => {
                            return this.store.dispatch(new BaseStateActions.Common.SetActiveBS(user?.businessSubjects[0]));
                        }),
                        tap(() => this.menuService.refreshMenu()),
                    );
                }
                return EMPTY;
            }),
            timeout(this.LOGIN_FORM_TIMEOUT),
        );
    }
}
